import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { withTheme, defaultTheme } from '@elixir/theme';
import getIconClasses from '../../iconUtils.js';
import styles from './Button-hashed.scss';

/**
 * The `Button` component is used to render an "Atomic Design" button.
 * Can be used with one of the available flavors (through the `type` prop): `secondary`, `tertiary`,
 * `neutral` (`primary` will be applied as the default when none is explicitly defined).
 * Custom sizing flavors can also be used through the `size` prop: `small` or `large` versions are available
 * (the default is a medium size).
 * May also display an icon (defined through the `icon` prop) which will be placed on the left or right of the button,
 * depending on the `iconPosition` prop.
 *
 */
export class Button extends PureComponent {
    static propTypes = {
        /**
        *  The children elements to render inside of the Button
        **/
        children: PropTypes.node,
        /**
        * CSS class name for this component
        **/
        className: PropTypes.string,
        /**
        * Flag indicating whether to disable this button
        **/
        disabled: PropTypes.bool,
        /**
        * Flag indicating whether the button should expand to the whole available width
        **/
        fullWidth: PropTypes.bool,
        /**
        * The short-name (minus the preceding `fa-`) of the optional Font Awesome icon
        **/
        icon: PropTypes.string,
        /**
        * Phrase which indicates where the icon should be placed relative to the text
        **/
        iconPosition: PropTypes.oneOf(['left', 'right']),
        /**
        * Callback when button is clicked
        **/
        onClick: PropTypes.func,
        /**
        * Phrase allowing to use smaller/larger variants
        **/
        size: PropTypes.oneOf(['small', 'large']),
        /**
        * Theme config that's supposed to be applied to the component
        **/
        theme: PropTypes.object,
        /**
        * Phrase allowing to set custom flavor (secondary, tertiary, neutral)
        **/
        type: PropTypes.oneOf(['primary', 'secondary', 'tertiary', 'neutral']),
    };

    static defaultProps = {
        disabled: false,
        fullWidth: false,
        iconPosition: 'left',
        size: undefined,
        type: 'primary',
        theme: defaultTheme
    };

    /**
     * Renders the Font Awesome icon.  If icon is for sorting, style it appropriately
     *
     * @param {string} [icon] - The short name for the Font Awesome icon
     * @param {string} [iconPosition] - The position right|left where the icon may be rendered
     * @param {object} [componentStyles] - component styles set that also includes styles definiton for icons
     * @returns {ReactElement|null} - The resulting rendered Icon
     */
    renderIcon = (icon, iconPosition, componentStyles) => {
        const classes = cx(
            componentStyles.icon,
            {[componentStyles.leftIcon]: iconPosition === 'left'},
            {[componentStyles.rightIcon]: iconPosition === 'right'},
            getIconClasses(icon)
        );
        return <i className={classes} />;
    }

    /**
     * Renders the actual Button's content - all the children and appropriately placed icon (if provided)
     * 
     * @param {ReactElement} [children] - The children elements to render inside of the Button
     * @param {string} [icon] - The short name for the Font Awesome icon (without `fa-` prefix)
     * @param {string} [iconPosition] - The position right|left where the icon may be rendered
     * @param {object} [componentStyles] - component styles set that also includes styles definiton for icons
     * @returns {ReactElement|null} - The resulting rendered Button content
     */
    renderChildrenAndIcon(children, icon, iconPosition, componentStyles) {
        return (
            <React.Fragment>
                {(icon && iconPosition === 'left') ? this.renderIcon(icon, iconPosition, componentStyles) : null}
                <span>{children}</span>
                {(icon && iconPosition === 'right') ? this.renderIcon(icon, iconPosition, componentStyles) : null}
            </React.Fragment>
        );
    }

    /**
     * Renders the content for this `Button` component
     *
     * @returns {ReactElement} - The resulting rendered Button
     */
    render() {
        const {
            children,
            className,
            disabled,
            fullWidth,
            icon,
            iconPosition,
            onClick,
            size,
            type,
            theme
        } = this.props;

        const themeStyles = theme.getStyles('Button', styles);
        const classes = cx(
            themeStyles.button,
            {[themeStyles.primary]: type === 'primary'},
            {[themeStyles.secondary]: type === 'secondary'},
            {[themeStyles.tertiary]: type === 'tertiary'},
            {[themeStyles.neutral]: type === 'neutral'},
            {[themeStyles.small]: size === 'small'},
            {[themeStyles.large]: size === 'large'},
            {[themeStyles.fullWidth]: fullWidth},
            {[themeStyles.disabled]: disabled},
            className
        );
        return (
            <button type="button" onClick={onClick} className={classes} disabled={disabled}>
                {this.renderChildrenAndIcon(children, icon, iconPosition, themeStyles)}
            </button>
        );
    }
}

export default withTheme(Button);
